import React, { useState, useEffect, useCallback } from 'react';
import { getDatabase, ref, get, set, push, remove } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import './ColorPaletteGenerator.css';

/**
 * Interactive Color Palette Generator - wie coolors.co
 * Generiert harmonische Farbpaletten basierend auf verschiedenen Stilen
 */
const ColorPaletteGenerator = ({ currentColors, onColorsChange, onClose, inline = false, userId = null, spaceId = null, onPaletteSaved = null, showGeneratorToggle = false, hasSavedPalettes = false }) => {
    const [selectedStyle, setSelectedStyle] = useState('vibrant');
    const [currentPalette, setCurrentPalette] = useState(currentColors);
    const [lockedColors, setLockedColors] = useState([false, false, false, false, false]);
    const [isGenerating, setIsGenerating] = useState(false);
    
    // PERSISTENT PALETTE SYSTEM (Firebase-based)
    const [savedPalettes, setSavedPalettes] = useState([]);
    const [selectedSavedPalette, setSelectedSavedPalette] = useState(null);
    
    // EDIT PALETTE SYSTEM
    const [editingPalette, setEditingPalette] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editPaletteColors, setEditPaletteColors] = useState(['#000000', '#000000', '#000000', '#000000', '#000000']);
    const [editPaletteName, setEditPaletteName] = useState('');
    const [customColorInputs, setCustomColorInputs] = useState(['', '', '', '', '']);
    
    // PALETTE HISTORY SYSTEM (simple back/forward navigation)
    const [paletteHistory, setPaletteHistory] = useState([]);
    const [historyPosition, setHistoryPosition] = useState(0);
    
    // GENERATOR TOGGLE STATE (for smart inline mode)
    const [showGenerator, setShowGenerator] = useState(false);

    // Farbstil-Definitionen
    const colorStyles = {
        vibrant: {
            name: 'Vibrant',
            description: 'Bold and energetic colors',
            saturation: [70, 100],
            lightness: [40, 70]
        },
        soft: {
            name: 'Soft',
            description: 'Gentle and calming colors',
            saturation: [30, 60],
            lightness: [60, 85]
        },
        pastel: {
            name: 'Pastel',
            description: 'Delicate and light colors',
            saturation: [15, 35],
            lightness: [80, 95]
        },
        monochrome: {
            name: 'Monochrome',
            description: 'Neutral grays and blacks',
            saturation: [0, 20],
            lightness: [20, 80]
        },
        nature: {
            name: 'Nature',
            description: 'Earthy and organic colors',
            saturation: [25, 60],
            lightness: [30, 70]
        }
    };

    // Professionelle 5-Farben-Palette generieren mit mehr Variation
    // Verschiedene Harmoniemodelle für mehr Vielfalt
    const generateHarmoniousPalette = useCallback((style, baseHue = null) => {
        const styleConfig = colorStyles[style];
        const colors = [];
        
        // Basis-Hue bestimmen mit mehr Variation
        const primaryHue = baseHue || Math.random() * 360;
        
        // Verschiedene Harmoniemodelle für mehr Vielfalt
        const harmonyModes = ['analogous', 'triadic', 'splitComplementary', 'tetradic', 'monochromatic'];
        const selectedHarmony = harmonyModes[Math.floor(Math.random() * harmonyModes.length)];
        
        if (style === 'monochrome') {
            // Monochrome: Graustufen mit harmonischem Farbakzent
            // Akzent zuerst: Harmonischer Farbton mit gleicher "Stärke" wie die Grautöne
            const accentHue = primaryHue; // Basis-Farbton
            colors.push(hslToHex(accentHue, 25, 45)); // Akzent - gleiche Helligkeit wie mittleres Grau, aber mit Farbe
            
            colors.push(hslToHex(0, 0, 15)); // Dunkel BG - sehr dunkel (15%)
            colors.push(hslToHex(0, 0, 25)); // Dunkel Text - dunkel (25%)
            colors.push(hslToHex(0, 0, 85)); // Hell Content - hell (85%)
            colors.push(hslToHex(0, 0, 95)); // Hell BG - sehr hell (95%)
            
        } else {
            // Professionelles 5-Farben-System mit verschiedenen Harmoniemodellen
            const baseHue = primaryHue % 360;
            
            // Generiere Hue-Variationen basierend auf Harmoniemodell
            let hueVariations = [];
            switch(selectedHarmony) {
                case 'analogous':
                    hueVariations = [
                        baseHue,
                        (baseHue + 15 + Math.random() * 30) % 360, // 15-45°
                        (baseHue + 30 + Math.random() * 30) % 360, // 30-60°
                        (baseHue - 15 + Math.random() * 30) % 360, // -15 bis +15°
                        (baseHue - 30 + Math.random() * 30) % 360  // -30 bis 0°
                    ];
                    break;
                case 'triadic':
                    hueVariations = [
                        baseHue,
                        (baseHue + 120 + Math.random() * 20 - 10) % 360, // 120° ±10°
                        (baseHue + 240 + Math.random() * 20 - 10) % 360, // 240° ±10°
                        (baseHue + 60 + Math.random() * 40) % 360,       // Zwischenton
                        (baseHue + 300 + Math.random() * 40) % 360       // Zwischenton
                    ];
                    break;
                case 'splitComplementary':
                    hueVariations = [
                        baseHue,
                        (baseHue + 150 + Math.random() * 20) % 360, // Split 1
                        (baseHue + 210 + Math.random() * 20) % 360, // Split 2
                        (baseHue + 30 + Math.random() * 30) % 360,  // Analog 1
                        (baseHue + 330 + Math.random() * 30) % 360  // Analog 2
                    ];
                    break;
                case 'tetradic':
                    hueVariations = [
                        baseHue,
                        (baseHue + 90 + Math.random() * 20) % 360,  // 90° ±10°
                        (baseHue + 180 + Math.random() * 20) % 360, // 180° ±10°
                        (baseHue + 270 + Math.random() * 20) % 360, // 270° ±10°
                        (baseHue + 45 + Math.random() * 30) % 360   // Zwischenton
                    ];
                    break;
                default: // monochromatic
                    hueVariations = [
                        baseHue,
                        baseHue + Math.random() * 10 - 5, // Leichte Variation
                        baseHue + Math.random() * 10 - 5,
                        baseHue + Math.random() * 15 - 7.5,
                        baseHue + Math.random() * 15 - 7.5
                    ];
            }
            
            // Sättigungswerte mit mehr Variation
            const satRange = styleConfig.saturation[1] - styleConfig.saturation[0];
            const baseSat = styleConfig.saturation[0] + Math.random() * satRange;
            const satVariations = [
                Math.max(10, baseSat + Math.random() * 30 - 15), // ±15% Variation
                Math.max(5, baseSat + Math.random() * 40 - 20),  // ±20% Variation
                Math.max(5, baseSat + Math.random() * 25 - 12.5), // ±12.5% Variation
                Math.max(3, baseSat + Math.random() * 35 - 17.5), // ±17.5% Variation
                Math.max(2, baseSat + Math.random() * 20 - 10)    // ±10% Variation
            ];
            
            // NEUE REIHENFOLGE - Akzent zuerst!
            
            // Spezielle Behandlung für Pastel-Stil mit mehr Variation
            if (style === 'pastel') {
                // Pastel: Mehr Variation in Sättigung und Helligkeit
                const basePastelSat = 10 + Math.random() * 25; // 10-35% für mehr Range
                
                // Verwende verschiedene Hues und variiere Sättigung stärker
                colors.push(hslToHex(hueVariations[0], Math.min(45, basePastelSat + Math.random() * 20), 70 + Math.random() * 15)); // Akzent
                colors.push(hslToHex(hueVariations[1], basePastelSat * 0.8, 55 + Math.random() * 15)); // Dunkel BG
                colors.push(hslToHex(hueVariations[2], basePastelSat * 0.9, 65 + Math.random() * 15)); // Dunkel Text
                colors.push(hslToHex(hueVariations[3], basePastelSat * 0.6, 85 + Math.random() * 8)); // Hell Content
                colors.push(hslToHex(hueVariations[4], basePastelSat * 0.4, 92 + Math.random() * 6)); // Hell BG
                
            } else if (style === 'nature') {
                // Natur: Viel mehr Variation in erdigen Tönen
                const earthyHueRanges = [
                    [10, 50],   // Rot-Orange-Braun (erweitert)
                    [50, 90],   // Orange-Gelb-Braun (erweitert)  
                    [90, 150],  // Gelb-Grün (erweitert)
                    [150, 200], // Grün-Türkis (erweitert)
                    [200, 240], // Blau-Grün (neu hinzugefügt)
                ];
                
                // Wähle zufällige Bereiche für mehr Variation
                const selectedRanges = earthyHueRanges.sort(() => Math.random() - 0.5).slice(0, 5);
                const earthyHues = selectedRanges.map(([min, max]) => min + Math.random() * (max - min));
                
                // Mehr Variation in Sättigung und Helligkeit
                colors.push(hslToHex(earthyHues[0], 35 + Math.random() * 35, 40 + Math.random() * 25)); // Akzent
                colors.push(hslToHex(earthyHues[1], 25 + Math.random() * 30, 15 + Math.random() * 20)); // Dunkel BG
                colors.push(hslToHex(earthyHues[2], 30 + Math.random() * 25, 20 + Math.random() * 20)); // Dunkel Text
                colors.push(hslToHex(earthyHues[3], 10 + Math.random() * 20, 70 + Math.random() * 15)); // Hell Content
                colors.push(hslToHex(earthyHues[4], 5 + Math.random() * 15, 85 + Math.random() * 12)); // Hell BG
                
            } else {
                // Normale Stile mit neuen Harmoniemodellen und mehr Variation
                
                // Helligkeitswerte mit mehr Variation
                const lightnessRange = styleConfig.lightness[1] - styleConfig.lightness[0];
                const baseLightness = styleConfig.lightness[0] + Math.random() * lightnessRange;
                const lightnessVariations = [
                    Math.max(20, baseLightness + Math.random() * 25 - 12.5), // Akzent
                    Math.max(8, baseLightness * 0.3 + Math.random() * 15),   // Dunkel BG
                    Math.max(15, baseLightness * 0.4 + Math.random() * 20),  // Dunkel Text
                    Math.min(95, baseLightness * 1.2 + Math.random() * 15),  // Hell Content
                    Math.min(98, baseLightness * 1.3 + Math.random() * 10)   // Hell BG
                ];
                
                // Verwende die generierten Variationen für mehr Vielfalt
                colors.push(hslToHex(hueVariations[0], satVariations[0], lightnessVariations[0])); // Akzent
                colors.push(hslToHex(hueVariations[1], satVariations[1], lightnessVariations[1])); // Dunkel BG
                colors.push(hslToHex(hueVariations[2], satVariations[2], lightnessVariations[2])); // Dunkel Text
                colors.push(hslToHex(hueVariations[3], satVariations[3], lightnessVariations[3])); // Hell Content
                colors.push(hslToHex(hueVariations[4], satVariations[4], lightnessVariations[4])); // Hell BG
            }
        }
        
        return colors;
    }, []);

    // HSL zu Hex konvertieren
    const hslToHex = (h, s, l) => {
        h = h / 360;
        s = s / 100;
        l = l / 100;
        
        const hue2rgb = (p, q, t) => {
            if (t < 0) t += 1;
            if (t > 1) t -= 1;
            if (t < 1/6) return p + (q - p) * 6 * t;
            if (t < 1/2) return q;
            if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
            return p;
        };
        
        let r, g, b;
        if (s === 0) {
            r = g = b = l;
        } else {
            const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
            const p = 2 * l - q;
            r = hue2rgb(p, q, h + 1/3);
            g = hue2rgb(p, q, h);
            b = hue2rgb(p, q, h - 1/3);
        }
        
        const toHex = (c) => {
            const hex = Math.round(c * 255).toString(16);
            return hex.length === 1 ? '0' + hex : hex;
        };
        
        return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
    };

    // Add palette to history (simple system)
    const addToHistory = useCallback((palette) => {
        console.log('📝 Adding palette to history:', { 
            position: historyPosition, 
            historyLength: paletteHistory.length,
            newPalette: palette 
        });
        
        setPaletteHistory(prev => {
            const newHistory = [palette, ...prev];
            
            console.log('📝 New history created:', { 
                newHistoryLength: newHistory.length
            });
            
            // Keep only last 10 palettes for performance
            return newHistory.slice(0, 10);
        });
        
        setHistoryPosition(0); // Reset to newest position
    }, [historyPosition, paletteHistory]);

    // Neue Palette generieren (with browser-like history)
    const generateNewPalette = useCallback(() => {
        setIsGenerating(true);
        
        setTimeout(() => {
            const newColors = generateHarmoniousPalette(selectedStyle);
            const updatedPalette = currentPalette.map((color, index) => 
                lockedColors[index] ? color : newColors[index]
            );
            
            // Add current palette to history before changing
            addToHistory(currentPalette);
            
            setCurrentPalette(updatedPalette);
            setIsGenerating(false);
        }, 200);
    }, [selectedStyle, currentPalette, lockedColors, generateHarmoniousPalette, addToHistory]);

    // Navigate back in history (simpler logic)
    const navigateBack = useCallback(() => {
        console.log('🔙 Navigate back clicked!', { historyPosition, historyLength: paletteHistory.length });
        
        const nextPosition = historyPosition + 1;
        
        if (nextPosition >= paletteHistory.length) {
            console.log('🔙 No more palettes in back history');
            return;
        }
        
        const previousPalette = paletteHistory[nextPosition];
        setCurrentPalette(previousPalette);
        setHistoryPosition(nextPosition);
        
        console.log(`🔙 Navigated back to position ${nextPosition}/${paletteHistory.length - 1}`);
    }, [historyPosition, paletteHistory]);

    // Navigate forward in history (simpler logic)
    const navigateForward = useCallback(() => {
        console.log('🔜 Navigate forward clicked!', { historyPosition, historyLength: paletteHistory.length });
        
        const nextPosition = historyPosition - 1;
        
        if (nextPosition < 0) {
            console.log('🔜 No more palettes in forward history');
            return;
        }
        
        const nextPalette = paletteHistory[nextPosition];
        setCurrentPalette(nextPalette);
        setHistoryPosition(nextPosition);
        
        console.log(`🔜 Navigated forward to position ${nextPosition}/${paletteHistory.length - 1}`);
    }, [historyPosition, paletteHistory]);

    // Check if navigation is possible (simpler logic)
    const canGoBack = paletteHistory.length > 0 && historyPosition < paletteHistory.length - 1;
    const canGoForward = historyPosition > 0;

    // Keyboard Event Listeners - Spacebar & Backspace
    useEffect(() => {
        const handleKeyPress = (event) => {
            // Don't interfere with input fields, textareas, or contenteditable elements
            const activeElement = document.activeElement;
            const isInputField = activeElement && (
                activeElement.tagName === 'INPUT' ||
                activeElement.tagName === 'TEXTAREA' ||
                activeElement.contentEditable === 'true'
            );
            
            if (!isInputField) {
                if (event.code === 'Space') {
                    event.preventDefault();
                    generateNewPalette();
                } else if (event.code === 'Backspace' || event.code === 'Delete') {
                    event.preventDefault();
                    navigateBack();
                } else if (event.code === 'ArrowLeft') {
                    event.preventDefault();
                    navigateBack();
                } else if (event.code === 'ArrowRight') {
                    event.preventDefault();
                    navigateForward();
                }
            }
        };

        document.addEventListener('keydown', handleKeyPress);
        return () => document.removeEventListener('keydown', handleKeyPress);
    }, [generateNewPalette, navigateBack, navigateForward]);

    // Lock/Unlock Farbe
    const toggleColorLock = (index) => {
        const newLockedColors = [...lockedColors];
        newLockedColors[index] = !newLockedColors[index];
        setLockedColors(newLockedColors);
    };

    // Einzelne Farbe ändern
    const handleColorChange = (index, newColor) => {
        const newPalette = [...currentPalette];
        newPalette[index] = newColor;
        setCurrentPalette(newPalette);
    };

    // Load saved palettes from Firebase (space-specific)
    const loadSavedPalettes = useCallback(async () => {
        const auth = getAuth();
        const currentUser = auth.currentUser;
        if (!currentUser) {
            console.log('🔍 [PALETTE-LOAD] No current user, skipping load');
            return;
        }
        
        // If no spaceId provided, don't load any saved palettes (creation mode)
        if (!spaceId) {
            setSavedPalettes([]);
            console.log('🔍 [PALETTE-LOAD] No spaceId provided: Creation mode - no saved palettes');
            return;
        }
        
        try {
            const db = getDatabase();
            
            // FIRST: Try to load from global path (old location)
            const globalPath = `/users/${currentUser.uid}/savedColorPalettes`;
            const globalRef = ref(db, globalPath);
            const globalSnapshot = await get(globalRef);
            
            if (globalSnapshot.exists()) {
                console.log('🔍 [PALETTE-LOAD] Found palettes in GLOBAL location:', globalPath);
                const globalPalettes = globalSnapshot.val();
                console.log('🔍 [PALETTE-LOAD] Global palettes data:', globalPalettes);
                
                // Move them to space-specific location
                const spacePalettesPath = `/users/${currentUser.uid}/spaces/${spaceId}/savedColorPalettes`;
                const spacePalettesRef = ref(db, spacePalettesPath);
                await set(spacePalettesRef, globalPalettes);
                console.log('✅ [PALETTE-LOAD] Moved palettes from global to space:', spacePalettesPath);
                
                // Remove from global location
                await remove(globalRef);
                console.log('✅ [PALETTE-LOAD] Cleaned up global palettes');
                
                // Load the moved palettes
                const palettesArray = Object.entries(globalPalettes).map(([key, value]) => ({
                    id: key,
                    ...value
                }));
                setSavedPalettes(palettesArray);
                console.log(`✅ [PALETTE-LOAD] Loaded ${palettesArray.length} moved palettes`);
                return;
            }
            
            // SECOND: Try space-specific path
            const palettesPath = `/users/${currentUser.uid}/spaces/${spaceId}/savedColorPalettes`;
            console.log('🔍 [PALETTE-LOAD] Loading palettes from path:', palettesPath);
            console.log('🔍 [PALETTE-LOAD] Current user ID:', currentUser.uid);
            console.log('🔍 [PALETTE-LOAD] Space ID:', spaceId);
            const palettesRef = ref(db, palettesPath);
            const snapshot = await get(palettesRef);
            
            if (snapshot.exists()) {
                const palettesData = snapshot.val();
                console.log('🔍 [PALETTE-LOAD] Raw palettes data from Firebase:', palettesData);
                console.log('🔍 [PALETTE-LOAD] Palettes data keys:', Object.keys(palettesData));
                console.log('🔍 [PALETTE-LOAD] Palettes data values:', Object.values(palettesData));
                
                const palettesArray = Object.entries(palettesData).map(([key, value]) => ({
                    id: key,
                    ...value
                }));
                console.log('🔍 [PALETTE-LOAD] Processed palettes array:', palettesArray);
                console.log('🔍 [PALETTE-LOAD] Array length:', palettesArray.length);
                
                setSavedPalettes(palettesArray);
                console.log(`✅ [PALETTE-LOAD] Loaded ${palettesArray.length} saved color palettes for space ${spaceId}`);
            } else {
                setSavedPalettes([]);
                console.log('🔍 [PALETTE-LOAD] No palettes found in Firebase at path:', palettesPath);
            }
        } catch (error) {
            console.error('❌ [PALETTE-LOAD] Error loading saved palettes:', error);
        }
    }, [spaceId]);
    
    useEffect(() => {
        loadSavedPalettes();
    }, [loadSavedPalettes]);

    // Auto-load first saved palette when available (after palettes are loaded)
    useEffect(() => {
        if (savedPalettes.length > 0 && !selectedSavedPalette) {
            // Check if current palette is default/empty or needs to be replaced
            const hasValidColors = currentPalette.length === 5 && 
                                  currentPalette.every(color => color && color.startsWith('#') && color.length === 7);
            
            if (!hasValidColors) {
                console.log('🎨 [AUTO-LOAD] Loading first saved palette:', savedPalettes[0]);
                loadSavedPalette(savedPalettes[0]);
            } else {
                // Check if current colors match any saved palette
                const matchingPalette = savedPalettes.find(palette => {
                    const paletteColors = [
                        palette.colors.color1,
                        palette.colors.color2,
                        palette.colors.color3,
                        palette.colors.color4,
                        palette.colors.color5
                    ];
                    return JSON.stringify(paletteColors) === JSON.stringify(currentPalette);
                });
                
                if (matchingPalette) {
                    console.log('🎨 [AUTO-LOAD] Current palette matches saved palette:', matchingPalette);
                    setSelectedSavedPalette(matchingPalette.id);
                    setSelectedStyle(matchingPalette.style);
                } else {
                    console.log('🎨 [AUTO-LOAD] Current palette is custom, keeping as-is');
                }
            }
        }
    }, [savedPalettes, selectedSavedPalette, currentPalette]);

    // Generate automatic palette name
    const generatePaletteName = () => {
        const nextNumber = savedPalettes.length + 1;
        return `Palette ${nextNumber}`;
    };
    
    // Generate consistent palette key (like SpaceCreationWizard)
    const generatePaletteKey = () => {
        const timestamp = Date.now();
        const index = savedPalettes.length;
        return `palette_${timestamp}_${index}`;
    };

    // Generate unique palette name (for creation mode)
    const generateUniquePaletteName = () => {
        const nextNumber = savedPalettes.length + 1;
        return `Palette ${nextNumber}`;
    };

    // Save current palette to Firebase (persistent, space-specific)
    const savePalettePersistently = async () => {
        const auth = getAuth();
        const currentUser = auth.currentUser;
        if (!currentUser) return;
        
        // In creation mode (no spaceId), save to local state only
        if (!spaceId) {
            const paletteName = generateUniquePaletteName(); // Use unique name for local palettes
            const paletteData = {
                id: `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`, // Unique ID for local storage
                name: paletteName,
                colors: {
                    color1: currentPalette[0],
                    color2: currentPalette[1],
                    color3: currentPalette[2],
                    color4: currentPalette[3],
                    color5: currentPalette[4]
                },
                style: selectedStyle,
                createdAt: new Date().toISOString(),
                isLocal: true // Flag for local-only palette
            };
            
            setSavedPalettes(prev => [...prev, paletteData]);
            console.log('🎨 Creation mode: Palette saved locally:', paletteName);
            return;
        }
        
        const paletteName = generatePaletteName(); // Use normal naming for space palettes
        
        try {
            const db = getDatabase();
            // Use space-specific path if spaceId is provided, otherwise fall back to global
            const palettesPath = spaceId 
                ? `/users/${currentUser.uid}/spaces/${spaceId}/savedColorPalettes`
                : `/users/${currentUser.uid}/savedColorPalettes`;
            
            // Use consistent key generation instead of push() for better compatibility
            const paletteKey = generatePaletteKey();
            const paletteRef = ref(db, `${palettesPath}/${paletteKey}`);
            
            const paletteData = {
                name: paletteName,
                colors: {
                    color1: currentPalette[0],
                    color2: currentPalette[1],
                    color3: currentPalette[2],
                    color4: currentPalette[3],
                    color5: currentPalette[4]
                },
                style: selectedStyle,
                createdAt: new Date().toISOString(),
                spaceId: spaceId // Store spaceId for reference
            };
            
            console.log('🔍 [PALETTE-SAVE] Attempting to save palette:', paletteData);
            console.log('🔍 [PALETTE-SAVE] Firebase path:', palettesPath);
            console.log('🔍 [PALETTE-SAVE] Generated key:', paletteKey);
            
            await set(paletteRef, paletteData);
            
            console.log('✅ [PALETTE-SAVE] Palette data saved to Firebase successfully');
            console.log('🔍 [PALETTE-SAVE] Saved palette data:', paletteData);
            console.log('🔍 [PALETTE-SAVE] Firebase key generated:', paletteKey);
            
            // Update local state
            const newPalette = { id: paletteKey, ...paletteData };
            setSavedPalettes(prev => {
                const updated = [...prev, newPalette];
                console.log('🔍 [PALETTE-SAVE] Updated local savedPalettes:', updated);
                console.log('🔍 [PALETTE-SAVE] New local state length:', updated.length);
                return updated;
            });
            
            console.log(`✅ [PALETTE-SAVE] Palette saved persistently to Firebase for space ${spaceId}:`, paletteName);
            
            // Notify parent component that palette was saved
            if (onPaletteSaved) {
                onPaletteSaved();
            }
            
            // RELOAD PALETTES FROM FIREBASE TO VERIFY SAVE
            console.log('🔄 [PALETTE-SAVE] Reloading palettes to verify save...');
            setTimeout(() => {
                loadSavedPalettes();
            }, 500); // Small delay to ensure Firebase write is complete
        } catch (error) {
            console.error('Error saving palette to Firebase:', error);
        }
    };

    // Load saved palette from Firebase
    const loadSavedPalette = (palette, preserveAssignments = true) => {
        console.log('🎨 [PALETTE-LOAD] Loading saved palette:', palette);
        console.log('🎨 [PALETTE-LOAD] Preserve assignments:', preserveAssignments);
        
        const paletteColors = [
            palette.colors.color1,
            palette.colors.color2,
            palette.colors.color3,
            palette.colors.color4,
            palette.colors.color5
        ];
        setCurrentPalette(paletteColors);
        setSelectedSavedPalette(palette.id);
        setSelectedStyle(palette.style);
        
        // Directly apply with feedback for saved palette selection
        if (inline && onColorsChange) {
            const colorObject = {
                color1: palette.colors.color1,
                color2: palette.colors.color2,
                color3: palette.colors.color3,
                color4: palette.colors.color4,
                color5: palette.colors.color5
            };
            console.log('🎨 [PALETTE-LOAD] Calling onColorsChange with:', colorObject);
            // preserveAssignments = true bedeutet, dass bestehende Color Assignments beibehalten werden
            onColorsChange(colorObject, true, preserveAssignments); // Show feedback when loading saved palette
        } else {
            console.log('🎨 [PALETTE-LOAD] Not calling onColorsChange:', { inline, hasCallback: !!onColorsChange });
        }
    };

    // Delete saved palette from Firebase (space-specific)
    const deleteSavedPalette = async (paletteId) => {
        const auth = getAuth();
        const currentUser = auth.currentUser;
        if (!currentUser) return;
        
        try {
            const db = getDatabase();
            
            // In creation mode, delete from local state only
            if (!spaceId) {
                setSavedPalettes(prev => prev.filter(p => p.id !== paletteId));
                if (selectedSavedPalette === paletteId) {
                    setSelectedSavedPalette(null);
                }
                console.log('🎨 Creation mode: Palette deleted locally:', paletteId);
                return;
            }
            
            // Use space-specific path if spaceId is provided, otherwise fall back to global
            const palettePath = spaceId 
                ? `/users/${currentUser.uid}/spaces/${spaceId}/savedColorPalettes/${paletteId}`
                : `/users/${currentUser.uid}/savedColorPalettes/${paletteId}`;
            const paletteRef = ref(db, palettePath);
            await remove(paletteRef);
            
            setSavedPalettes(prev => prev.filter(p => p.id !== paletteId));
            if (selectedSavedPalette === paletteId) {
                setSelectedSavedPalette(null);
            }
            
            console.log(`✅ Palette deleted from Firebase for ${spaceId ? `space ${spaceId}` : 'global'}`);
        } catch (error) {
            console.error('Error deleting palette from Firebase:', error);
        }
    };

    // EDIT PALETTE FUNCTIONS
    const startEditPalette = (palette) => {
        setEditingPalette(palette);
        setEditPaletteColors([
            palette.colors.color1,
            palette.colors.color2,
            palette.colors.color3,
            palette.colors.color4,
            palette.colors.color5
        ]);
        setEditPaletteName(palette.name);
        setCustomColorInputs(['', '', '', '', '']); // Reset custom inputs
        setShowEditModal(true);
        console.log('🎨 [PALETTE-EDIT] Started editing palette:', palette.name);
    };

    const handleCustomColorInput = (index, value) => {
        const newInputs = [...customColorInputs];
        newInputs[index] = value;
        setCustomColorInputs(newInputs);

        // Validate and apply hex color if valid
        if (isValidHexColor(value)) {
            const newColors = [...editPaletteColors];
            newColors[index] = value.startsWith('#') ? value : `#${value}`;
            setEditPaletteColors(newColors);
        }
    };

    const isValidHexColor = (hex) => {
        // Remove # if present
        const cleanHex = hex.replace('#', '');
        // Check if it's 3 or 6 characters and only contains valid hex characters
        return /^([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(cleanHex);
    };

    const handleEditColorChange = (index, color) => {
        const newColors = [...editPaletteColors];
        newColors[index] = color;
        setEditPaletteColors(newColors);
        
        // Update custom input to match
        const newInputs = [...customColorInputs];
        newInputs[index] = color.replace('#', '');
        setCustomColorInputs(newInputs);
    };

    const saveEditedPalette = async () => {
        if (!editingPalette || !editPaletteName.trim()) return;

        const auth = getAuth();
        const currentUser = auth.currentUser;
        
        if (!currentUser) {
            console.error('❌ [PALETTE-UPDATE] User not authenticated');
            return;
        }

        try {
            const db = getDatabase();
            const palettesPath = spaceId 
                ? `/users/${currentUser.uid}/spaces/${spaceId}/savedColorPalettes`
                : `/users/${currentUser.uid}/savedColorPalettes`;
            
            const paletteRef = ref(db, `${palettesPath}/${editingPalette.id}`);
            
            const updatedPaletteData = {
                ...editingPalette,
                name: editPaletteName.trim(),
                colors: {
                    color1: editPaletteColors[0],
                    color2: editPaletteColors[1],
                    color3: editPaletteColors[2],
                    color4: editPaletteColors[3],
                    color5: editPaletteColors[4]
                },
                updatedAt: new Date().toISOString()
            };
            
            await set(paletteRef, updatedPaletteData);
            
            // Update local state
            setSavedPalettes(prev => prev.map(p => 
                p.id === editingPalette.id 
                    ? { ...updatedPaletteData, id: editingPalette.id }
                    : p
            ));
            
            // Update selected palette if it was the edited one
            if (selectedSavedPalette && selectedSavedPalette.id === editingPalette.id) {
                setSelectedSavedPalette({ ...updatedPaletteData, id: editingPalette.id });
            }
            
            console.log('✅ [PALETTE-UPDATE] Palette updated successfully:', updatedPaletteData.name);
            closeEditModal();
            
        } catch (error) {
            console.error('❌ [PALETTE-UPDATE] Error updating palette:', error);
        }
    };

    const closeEditModal = () => {
        setShowEditModal(false);
        setEditingPalette(null);
        setEditPaletteColors(['#000000', '#000000', '#000000', '#000000', '#000000']);
        setEditPaletteName('');
        setCustomColorInputs(['', '', '', '', '']);
    };

    // Apply current palette (for inline mode)
    const applyCurrentPalette = (showFeedback = false, preserveAssignments = true) => {
        const colorObject = {
            color1: currentPalette[0], // Akzent - Kontrastfarbe für Highlights
            color2: currentPalette[1], // Dunkel BG - Dunkler Hintergrund
            color3: currentPalette[2], // Dunkel Text - Dunkler Text/Elemente
            color4: currentPalette[3], // Hell Content - Heller Content-Bereich
            color5: currentPalette[4]  // Hell BG - Heller Hintergrund
        };
        onColorsChange(colorObject, showFeedback, preserveAssignments);
    };

    // Palette speichern (original modal function)
    const savePalette = () => {
        applyCurrentPalette();
        if (onClose) onClose();
    };

    // Initial palette generieren nur wenn wirklich leer oder ungültig
    useEffect(() => {
        // Nur generieren wenn die Palette wirklich leer ist oder ungültige Farben enthält
        // NICHT generieren wenn bereits gültige Farben vorhanden sind (auch wenn eine davon weiß ist)
        const hasValidColors = currentPalette.length === 5 && 
                              currentPalette.every(color => color && color.startsWith('#') && color.length === 7);
        
        if (!hasValidColors) {
            console.log('🎨 [COLOR-GENERATOR] Generating initial palette - no valid colors found:', currentPalette);
            generateNewPalette();
        } else {
            console.log('🎨 [COLOR-GENERATOR] Valid palette exists - skipping generation:', currentPalette);
        }
    }, []);

    // Auto-apply palette changes in inline mode (no feedback for spacebar generation)
    useEffect(() => {
        if (inline && currentPalette.length === 5 && currentPalette.every(color => color && color.startsWith('#') && color.length === 7)) {
            applyCurrentPalette(false); // No feedback for auto-apply
        }
    }, [currentPalette, inline]);

    // Inline-Rendering (ohne Overlay/Modal)
    if (inline) {
        return (
            <div className="color-palette-generator-inline">
                {/* Smart Saved Palettes Section with Toggle */}
                {savedPalettes.length > 0 ? (
                    <div className="saved-palettes-section">
                        <div className="saved-palettes-header">
                            <h4 className="saved-palettes-title">Saved Palettes</h4>
                            {showGeneratorToggle && (
                                <button 
                                    className={`color-generator-toggle-btn ${showGenerator ? 'active' : ''}`}
                                    onClick={() => setShowGenerator(!showGenerator)}
                                    title={showGenerator ? 'Hide Color Generator' : 'Show Color Generator'}
                                >
                                    <i className={`fas ${showGenerator ? 'fa-minus' : 'fa-plus'}`}></i>
                                </button>
                            )}
                        </div>
                        <div className="saved-palettes-grid">
                            {savedPalettes.map((palette) => (
                                <div 
                                    key={palette.id}
                                    className={`saved-palette-item ${selectedSavedPalette === palette.id ? 'active' : ''}`}
                                    onClick={() => loadSavedPalette(palette, true)}
                                >
                                    <div className="saved-palette-preview">
                                        {Object.values(palette.colors).map((color, index) => (
                                            <div 
                                                key={index}
                                                className="saved-palette-color"
                                                style={{ backgroundColor: color }}
                                            ></div>
                                        ))}
                                    </div>
                                    <div className="saved-palette-info">
                                        <span className="saved-palette-name">{palette.name}</span>
                                        <div className="palette-actions">
                                            <button 
                                                className="edit-palette-btn"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    startEditPalette(palette);
                                                }}
                                                title="Edit palette"
                                            >
                                                <i className="fas fa-edit"></i>
                                            </button>
                                            <button 
                                                className="delete-palette-btn"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    deleteSavedPalette(palette.id);
                                                }}
                                                title="Delete palette"
                                            >
                                                <i className="fas fa-trash"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="no-palettes-message">
                        <div className="no-palettes-content">
                            <i className="fas fa-palette no-palettes-icon"></i>
                            <h5>No Color Palettes Yet</h5>
                            <p>Create your first color palette using the generator below.</p>
                            {!showGenerator && showGeneratorToggle && (
                                <button 
                                    className="create-first-palette-btn"
                                    onClick={() => setShowGenerator(true)}
                                >
                                    <i className="fas fa-plus"></i>
                                    Create Color Palette
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {/* Color Generator Section - show when: toggle is active, no toggle exists, OR no palettes saved */}
                {(!showGeneratorToggle || showGenerator || savedPalettes.length === 0) && (
                    <>
                        {/* Farbstil-Auswahl */}
                        <div className="style-selector-inline">
                            <div className="style-grid-inline">
                                {Object.entries(colorStyles).map(([key, style]) => (
                                    <button
                                        key={key}
                                        className={`style-option-inline ${selectedStyle === key ? 'active' : ''}`}
                                        onClick={() => setSelectedStyle(key)}
                                        title={style.description}
                                    >
                                        <span className="style-name">{style.name}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Farbpalette - nur die 5 Balken */}
                        <div className="color-palette-inline">
                            {currentPalette.map((color, index) => {
                                const colorLabels = [
                                    'Accent', // Kontrastfarbe für Highlights
                                    'Dark BG', // Dunkler Hintergrund
                                    'Dark Text', // Dunkler Text/Elemente  
                                    'Light Content', // Heller Content-Bereich
                                    'Light BG' // Heller Hintergrund
                                ];
                                return (
                                    <div key={index} className="palette-color-bar">
                                        <div 
                                            className="palette-color-surface-inline"
                                            style={{ backgroundColor: color }}
                                        >
                                            <div className="palette-color-info-inline">
                                                <span className="palette-color-label">{colorLabels[index]}</span>
                                                <span className="palette-color-hex">{color}</span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        
                        {/* Control Buttons */}
                        <div className="palette-controls-inline">
                            <div className="spacebar-hint-inline">
                                <i className="fas fa-keyboard"></i>
                                Press Spacebar for new colors
                            </div>
                            <div className="palette-action-buttons">
                                {/* Navigation Buttons */}
                                <div className="palette-navigation">
                                    <button 
                                        className="nav-btn back-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            navigateBack();
                                        }}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        disabled={!canGoBack}
                                        title="Go back in palette history"
                                        type="button"
                                    >
                                        <i className="fas fa-chevron-left"></i>
                                    </button>
                                    <button 
                                        className="nav-btn forward-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            navigateForward();
                                        }}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        disabled={!canGoForward}
                                        title="Go forward in palette history"
                                        type="button"
                                    >
                                        <i className="fas fa-chevron-right"></i>
                                    </button>
                                </div>
                                
                                {/* Save Button */}
                                <button 
                                    className="save-palette-btn-inline"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        savePalettePersistently();
                                    }}
                                    title="Save current palette permanently"
                                >
                                    <i className="fas fa-bookmark"></i>
                                    Save Palette
                                </button>
                            </div>
                        </div>
                    </>
                )}

                {/* Edit Palette Modal for Inline Mode */}
                {showEditModal && (
                    <div className="edit-palette-modal-overlay" onClick={closeEditModal}>
                        <div className="edit-palette-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="edit-modal-header">
                                <h3>Palette bearbeiten</h3>
                                <button className="close-edit-btn" onClick={closeEditModal}>
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>

                            <div className="edit-modal-content">
                                {/* Palette Name */}
                                <div className="edit-name-section">
                                    <label>Palette Name:</label>
                                    <input
                                        type="text"
                                        className="edit-palette-name-input"
                                        value={editPaletteName}
                                        onChange={(e) => setEditPaletteName(e.target.value)}
                                        placeholder="Palette Name eingeben..."
                                    />
                                </div>

                                {/* Color Editors */}
                                <div className="edit-colors-section">
                                    <h4>Farben bearbeiten</h4>
                                    <div className="edit-colors-grid">
                                        {editPaletteColors.map((color, index) => {
                                            const colorLabels = ['Akzent', 'Dunkel BG', 'Dunkel Text', 'Hell Content', 'Hell BG'];
                                            return (
                                                <div key={index} className="edit-color-item">
                                                    <label className="edit-color-label">{colorLabels[index]}</label>
                                                    
                                                    {/* Color Preview */}
                                                    <div 
                                                        className="edit-color-preview"
                                                        style={{ backgroundColor: color }}
                                                    ></div>

                                                    {/* Color Picker */}
                                                    <input
                                                        type="color"
                                                        value={color}
                                                        onChange={(e) => handleEditColorChange(index, e.target.value)}
                                                        className="edit-color-picker"
                                                    />

                                                    {/* Custom Hex Input */}
                                                    <div className="custom-hex-input">
                                                        <span className="hex-prefix">#</span>
                                                        <input
                                                            type="text"
                                                            className={`hex-input ${!isValidHexColor(customColorInputs[index]) && customColorInputs[index] ? 'invalid' : ''}`}
                                                            value={customColorInputs[index]}
                                                            onChange={(e) => handleCustomColorInput(index, e.target.value)}
                                                            placeholder={color.replace('#', '')}
                                                            maxLength="6"
                                                        />
                                                    </div>
                                                    
                                                    {customColorInputs[index] && !isValidHexColor(customColorInputs[index]) && (
                                                        <div className="hex-error">Ungültiger Hex-Code</div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>

                            <div className="edit-modal-footer">
                                <button className="cancel-edit-btn" onClick={closeEditModal}>
                                    Abbrechen
                                </button>
                                <button 
                                    className="save-edit-btn" 
                                    onClick={saveEditedPalette}
                                    disabled={!editPaletteName.trim()}
                                >
                                    <i className="fas fa-save"></i>
                                    Speichern
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // Original Modal-Rendering
    return (
        <div className="color-palette-generator-overlay">
            <div className="color-palette-generator">
                <div className="generator-header">
                    <div className="header-info">
                        <h2>Color Palette Generator</h2>
                        <p>Erstelle harmonische Farbpaletten für deinen Space</p>
                    </div>
                    <button className="close-btn" onClick={onClose}>
                        <i className="fas fa-times"></i>
                    </button>
                </div>

                {/* Farbstil-Auswahl */}
                <div className="style-selector">
                    <h3>Farbstil wählen</h3>
                    <div className="style-grid">
                        {Object.entries(colorStyles).map(([key, style]) => (
                            <button
                                key={key}
                                className={`style-option ${selectedStyle === key ? 'active' : ''}`}
                                onClick={() => setSelectedStyle(key)}
                            >
                                <span className="style-name">{style.name}</span>
                                <span className="style-desc">{style.description}</span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Farbpalette */}
                <div className="palette-container">
                    <div className="palette-header">
                        <h3>Deine Farbpalette</h3>
                        <div className="palette-controls">
                            <button 
                                className={`generate-btn ${isGenerating ? 'generating' : ''}`}
                                onClick={generateNewPalette}
                                disabled={isGenerating}
                            >
                                {isGenerating ? (
                                    <i className="fas fa-spinner fa-spin"></i>
                                ) : (
                                    <i className="fas fa-dice"></i>
                                )}
                                Neue Palette
                            </button>
                            <div className="spacebar-hint">
                                <i className="fas fa-keyboard"></i>
                                Spacebar für neue Farben
                            </div>
                        </div>
                    </div>

                    <div className="color-palette">
                        {currentPalette.map((color, index) => {
                            const colorLabels = [
                                'Akzent', // Kontrastfarbe für Highlights (ZUERST!)
                                'Dunkel BG', // Dunkler Hintergrund
                                'Dunkel Text', // Dunkler Text/Elemente  
                                'Hell Content', // Heller Content-Bereich
                                'Hell BG' // Heller Hintergrund
                            ];
                            return (
                                <div key={index} className="palette-color-card">
                                    <div 
                                        className="palette-color-surface"
                                        style={{ backgroundColor: color }}
                                    >
                                        {/* Top Section - Lock Button */}
                                        <div className="palette-color-header">
                                            <button
                                                className={`palette-lock-button ${lockedColors[index] ? 'palette-locked' : ''}`}
                                                onClick={() => toggleColorLock(index)}
                                                title={lockedColors[index] ? 'Entsperren' : 'Sperren'}
                                            >
                                                <i className={`fas ${lockedColors[index] ? 'fa-lock' : 'fa-unlock'}`}></i>
                                            </button>
                                        </div>
                                        
                                        {/* Bottom Section - Color Info */}
                                        <div className="palette-color-footer">
                                            <div className="palette-color-label">{colorLabels[index]}</div>
                                            <div className="palette-color-hex">{color.toUpperCase()}</div>
                                            <input
                                                type="color"
                                                value={color}
                                                onChange={(e) => handleColorChange(index, e.target.value)}
                                                className="palette-color-picker"
                                                title="Farbe anpassen"
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Actions */}
                <div className="generator-footer">
                    <div className="footer-info">
                        <i className="fas fa-lightbulb"></i>
                        <span>Tipp: Sperre Farben die dir gefallen und generiere nur neue für die anderen</span>
                    </div>
                    <div className="footer-actions">
                        <button className="cancel-btn" onClick={onClose}>
                            Abbrechen
                        </button>
                        <button className="save-btn" onClick={savePalette}>
                            <i className="fas fa-check"></i>
                            Palette verwenden
                        </button>
                    </div>
                </div>
            </div>

            {/* Edit Palette Modal */}
            {showEditModal && (
                <div className="edit-palette-modal-overlay" onClick={closeEditModal}>
                    <div className="edit-palette-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="edit-modal-header">
                            <h3>Palette bearbeiten</h3>
                            <button className="close-edit-btn" onClick={closeEditModal}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>

                        <div className="edit-modal-content">
                            {/* Palette Name */}
                            <div className="edit-name-section">
                                <label>Palette Name:</label>
                                <input
                                    type="text"
                                    className="edit-palette-name-input"
                                    value={editPaletteName}
                                    onChange={(e) => setEditPaletteName(e.target.value)}
                                    placeholder="Palette Name eingeben..."
                                />
                            </div>

                            {/* Color Editors */}
                            <div className="edit-colors-section">
                                <h4>Farben bearbeiten</h4>
                                <div className="edit-colors-grid">
                                    {editPaletteColors.map((color, index) => {
                                        const colorLabels = ['Akzent', 'Dunkel BG', 'Dunkel Text', 'Hell Content', 'Hell BG'];
                                        return (
                                            <div key={index} className="edit-color-item">
                                                <label className="edit-color-label">{colorLabels[index]}</label>
                                                
                                                {/* Color Preview */}
                                                <div 
                                                    className="edit-color-preview"
                                                    style={{ backgroundColor: color }}
                                                ></div>

                                                {/* Color Picker */}
                                                <input
                                                    type="color"
                                                    value={color}
                                                    onChange={(e) => handleEditColorChange(index, e.target.value)}
                                                    className="edit-color-picker"
                                                />

                                                {/* Custom Hex Input */}
                                                <div className="custom-hex-input">
                                                    <span className="hex-prefix">#</span>
                                                    <input
                                                        type="text"
                                                        className={`hex-input ${!isValidHexColor(customColorInputs[index]) && customColorInputs[index] ? 'invalid' : ''}`}
                                                        value={customColorInputs[index]}
                                                        onChange={(e) => handleCustomColorInput(index, e.target.value)}
                                                        placeholder={color.replace('#', '')}
                                                        maxLength="6"
                                                    />
                                                </div>
                                                
                                                {customColorInputs[index] && !isValidHexColor(customColorInputs[index]) && (
                                                    <div className="hex-error">Ungültiger Hex-Code</div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        <div className="edit-modal-footer">
                            <button className="cancel-edit-btn" onClick={closeEditModal}>
                                Abbrechen
                            </button>
                            <button 
                                className="save-edit-btn" 
                                onClick={saveEditedPalette}
                                disabled={!editPaletteName.trim()}
                            >
                                <i className="fas fa-save"></i>
                                Speichern
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ColorPaletteGenerator;
